import { forwardRef } from 'react';
import {
  ExclamationCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  BulbOutlined,
  AppstoreOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import { cn } from '@/libs/utils';

/**
 * @typedef {'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark'} AlertBoxVariant
 */

/**
 * @typedef {Object} AlertBoxProps
 * @property {boolean} [open=true] - Determines if the alert box is visible.
 * @property {boolean} [showIcon=true] - Whether to display the icon.
 * @property {boolean} [closable=false] - Whether the alert box has a close button.
 * @property {string} [className] - Additional custom classes.
 * @property {string} [title] - Title for the alert box (used for accessibility).
 * @property {AlertBoxVariant} [variant] - The visual variant of the alert box.
 * @property {React.ReactNode} children - Content to be displayed inside the alert box.
 * @property {() => void} [onClose] - Callback function triggered when the alert box is closed.
 * @property {React.Ref<HTMLDivElement>} [ref] - Forwarded ref for the alert box.
 */

/**
 * AlertBox component for displaying messages with optional icons and close button.
 *
 * @param {AlertBoxProps} props - Component properties.
 */
const AlertBox = forwardRef(
  (
    {
      className,
      title,
      open = true,
      variant = 'primary',
      showIcon = true,
      closable = false,
      onClose = () => {},
      metadata,
      children,
      ...rest
    },
    ref
  ) => {
    const variants = {
      primary: 'alert-box--primary',
      secondary: 'alert-box--secondary',
      success: 'alert-box--success',
      danger: 'alert-box--danger',
      warning: 'alert-box--warning',
      info: 'alert-box--info',
      question: 'alert-box--question',
      light: 'alert-box--light',
      dark: 'alert-box--dark',
    };

    const icons = {
      primary: <AppstoreOutlined />,
      secondary: <BulbOutlined />,
      success: <CheckCircleOutlined />,
      danger: <WarningOutlined />,
      warning: <ExclamationCircleOutlined />,
      info: <InfoCircleOutlined />,
      question: <QuestionCircleOutlined />,
      light: <BulbOutlined />,
      dark: <AppstoreOutlined />,
    };

    const icon = icons[variant] ?? null;
    const classes = variants[variant] ?? '';

    if (!open) {
      return null;
    }

    return (
      <div
        ref={ref}
        title={title}
        className={cn('alert-box', classes, className)}
        role="alert"
        {...rest}
      >
        <div className="flex justify-between">
          <div className={cn(showIcon ? 'float-left' : 'w-full')}>
            <div
              className={cn(
                'flex items-center justify-center',
                metadata?.wrapperClassName
              )}
            >
              {showIcon && (
                <span
                  className={cn('mr-2 self-start', metadata?.iconClassName)}
                >
                  {icon}
                </span>
              )}

              <div className={cn(metadata?.contentClassName)}>{children}</div>
            </div>
          </div>

          {closable && (
            <Button
              type="default"
              className={cn(
                'btn-default h-5 w-5 bg-transparent p-0 hover:!bg-transparent',
                classes
              )}
              onClick={onClose}
              icon={<CloseOutlined className="text-sm" />}
            >
              <span className="sr-only">Close</span>
            </Button>
          )}
        </div>
      </div>
    );
  }
);

AlertBox.displayName = 'AlertBox';

export default AlertBox;
